<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.packageTypes') }}</span>
          <el-button type="primary" @click="showAddDialog">
            <el-icon><Plus /></el-icon>
            {{ $t('common.add') }}
          </el-button>
        </div>
      </template>

      <!-- Filter Bar -->
      <div class="filter-bar">
        <el-radio-group v-model="activeFilter" size="default" @change="fetchPackageTypes">
          <el-radio-button :value="null">📋 {{ $t('vip.allTypes') }}</el-radio-button>
          <el-radio-button :value="true">{{ $t('vip.activeTypes') }}</el-radio-button>
          <el-radio-button :value="false">{{ $t('vip.inactiveTypes') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Table View -->
      <el-table 
        :data="vipStore.packageTypes" 
        v-loading="vipStore.packageTypesLoading" 
        stripe
        style="width: 100%"
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="name" :label="$t('vip.typeName')" min-width="150" show-overflow-tooltip />
        <el-table-column prop="description" :label="$t('vip.typeDescription')" min-width="200" show-overflow-tooltip />
        <el-table-column prop="displayOrder" :label="$t('poster.order')" width="100" align="center" />
        <el-table-column prop="isActive" :label="$t('vip.status')" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="Boolean(row.isActive) ? 'success' : 'info'" size="small">
              {{ Boolean(row.isActive) ? $t('vip.active') : $t('vip.inactive') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('vip.actions')" width="140" fixed="right" align="center">
          <template #default="{ row }">
            <el-tooltip :content="$t('common.edit')" placement="top">
              <el-button 
                type="primary" 
                size="small" 
                :icon="Edit"
                circle
                @click="handleEdit(row)"
              />
            </el-tooltip>
            <el-tooltip :content="$t('common.delete')" placement="top">
              <el-button 
                type="danger" 
                size="small" 
                :icon="Delete"
                circle
                @click="handleDelete(row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-if="!vipStore.packageTypes.length && !vipStore.packageTypesLoading" :description="$t('common.noData')" />
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="isEdit ? $t('common.edit') : $t('common.add')"
      width="500px"
      destroy-on-close
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
        <el-form-item :label="$t('vip.typeName')" prop="name">
          <el-input 
            v-model="form.name" 
            :placeholder="$t('vip.enterTypeName')"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('vip.typeDescription')" prop="description">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3"
            :placeholder="$t('vip.enterTypeDescription')"
          />
        </el-form-item>

        <el-form-item :label="$t('poster.order')" prop="displayOrder">
          <el-input-number 
            v-model="form.displayOrder" 
            :min="0" 
            :max="9999" 
            :step="1"
            style="width: 100%"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.status')" prop="isActive">
          <el-switch 
            v-model="form.isActive"
            :active-text="$t('vip.active')"
            :inactive-text="$t('vip.inactive')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          {{ $t('common.submit') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import { useVipStore } from '@/stores/vip'

const { t } = useI18n()
const vipStore = useVipStore()
const activeFilter = ref(null)
const dialogVisible = ref(false)
const isEdit = ref(false)
const submitting = ref(false)
const formRef = ref()

const form = reactive({
  id: null,
  name: '',
  description: '',
  displayOrder: 0,
  isActive: true
})

const rules = {
  name: [
    { required: true, message: 'Please enter package type name', trigger: 'blur' },
    { min: 1, max: 100, message: 'Name must be between 1-100 characters', trigger: 'blur' }
  ],
  displayOrder: [
    { required: true, message: 'Please enter display order', trigger: 'blur' },
    { type: 'number', min: 0, max: 9999, message: 'Display order must be between 0 and 9999', trigger: 'blur' }
  ]
}

onMounted(() => {
  fetchPackageTypes()
})

const fetchPackageTypes = () => {
  vipStore.fetchPackageTypes(activeFilter.value)
}

const showAddDialog = () => {
  isEdit.value = false
  resetForm()
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEdit.value = true
  form.id = row.id
  form.name = row.name
  form.description = row.description || ''
  form.displayOrder = row.displayOrder || 0
  form.isActive = Boolean(row.isActive)
  dialogVisible.value = true
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(t('vip.confirmDelete'), t('vip.confirmTitle'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    })
    
    const result = await vipStore.deletePackageType(row.id)
    
    if (result.success) {
      ElMessage.success(result.message || t('vip.deleteSuccess'))
      fetchPackageTypes()
    } else {
      // Show exact error message from API
      ElMessage.error(result.message || result.error || t('vip.deleteFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      // Extract and show the exact error message from the API response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.Message || 
                          error.message || 
                          t('vip.deleteFailed')
      ElMessage.error(errorMessage)
      console.error('Delete package type error:', error)
    }
  }
}

const submitForm = async () => {
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    try {
      let result
      if (isEdit.value) {
        result = await vipStore.updatePackageType(form.id, {
          id: form.id,
          name: form.name,
          description: form.description,
          displayOrder: form.displayOrder,
          isActive: form.isActive
        })
      } else {
        result = await vipStore.createPackageType({
          name: form.name,
          description: form.description,
          displayOrder: form.displayOrder,
          isActive: form.isActive
        })
      }
      
      if (result.success) {
        ElMessage.success(isEdit.value ? t('vip.editSuccess') : t('vip.addSuccess'))
        dialogVisible.value = false
        fetchPackageTypes()
      } else {
        ElMessage.error(result.message || (isEdit.value ? t('vip.editFailed') : t('vip.addFailed')))
      }
    } catch (error) {
      ElMessage.error(isEdit.value ? t('vip.editFailed') : t('vip.addFailed'))
    } finally {
      submitting.value = false
    }
  })
}

const resetForm = () => {
  form.id = null
  form.name = ''
  form.description = ''
  form.displayOrder = 0
  form.isActive = true
}
</script>

<style scoped>
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  transition: background-color 0.3s ease;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.filter-bar {
  margin-bottom: 16px;
  padding: 0;
}

:deep(.el-radio-group) {
  display: flex;
  gap: 4px;
}

:deep(.el-radio-button__inner) {
  padding: 8px 16px;
  font-size: 13px;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  :deep(.el-radio-group) {
    width: 100%;
  }

  :deep(.el-radio-button) {
    flex: 1;
  }

  :deep(.el-radio-button__inner) {
    padding: 6px 8px;
    font-size: 12px;
  }
}

/* Enhanced table styling */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table__header-wrapper) {
  background: var(--bg-color-base, #f5f7fa);
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--color-text-regular, #606266);
  padding: 10px 0;
  font-size: 13px;
}

:deep(.el-table__row) {
  transition: all 0.3s;
}

:deep(.el-table__row:hover) {
  background-color: var(--bg-color-light, #f5f7fa);
}

:deep(.el-table td) {
  padding: 10px 0;
  font-size: 13px;
}

/* Mobile-responsive table */
@media (max-width: 768px) {
  :deep(.el-table) {
    font-size: 12px;
  }
  
  :deep(.el-table .cell) {
    padding: 0 8px;
  }
  
  :deep(.el-table th) {
    padding: 8px 0;
    font-size: 12px;
  }
  
  :deep(.el-table td) {
    padding: 8px 0;
  }
  
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 12px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .page-container {
    padding: 8px;
  }

  .card-header span {
    font-size: 15px;
  }

  .filter-bar {
    margin-bottom: 12px;
  }

  :deep(.el-radio-button__inner) {
    padding: 6px 8px;
    font-size: 11px;
  }
}
</style>
